import _ from 'lodash'
import { createAction, createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AuthUser } from '@/core/classes/Auth'
import ChatRoomModel, { ChatRoom, ChatRoomState } from '@/core/models/chat-room.model'

import { RootState } from '@/redux/rootReducer'

export type ChatRoomReducerState = ChatRoomState

export const chatRoomAdapter = createEntityAdapter<ChatRoom, ChatRoom['_id']>({ selectId: (chatRoom) => chatRoom._id })
const chatRoomSelector = chatRoomAdapter.getSelectors<RootState>((state) => state.chatRoom.chatRooms)

export const selectChatRooms = chatRoomSelector.selectAll
export const selectChatRoomById = chatRoomSelector.selectById

export const selectChatRoomGetRequestTracker = (state: RootState) => state.chatRoom.requestTracker.get
export const selectChatRoomSendChatMessageRequestTracker = (state: RootState) => state.chatRoom.requestTracker.sendChatMessage

export const selectUnreadMessagesByChatRoom = createSelector(
  [(state: RootState) => state.auth.authUser, (state: RootState, chatRoomId: string) => selectChatRoomById(state, chatRoomId)],
  (authUser: AuthUser | null, chatRoom?: ChatRoom) => {
    if (!chatRoom || !authUser) return []
    return _.filter(chatRoom.messages, (message) => message.author !== authUser._id && !message.readBy.includes(authUser._id))
  }
)

const initialState: ChatRoomReducerState = _.merge({}, ChatRoomModel.getInitialState())

const chatRoomSlice = createSlice({
  name: ChatRoomModel.reducerName,
  initialState,
  reducers: {
    getChatRoom(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getChatRoomSuccess(state, action: PayloadAction<ChatRoom>) {
      chatRoomAdapter.upsertOne(state.chatRooms, action.payload)
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getChatRoomFailure(state, action) {
      state.requestTracker.get.loading = false
      state.requestTracker.get.error = action.payload
    },
    sendChatMessage: {
      reducer: (state, action: PayloadAction<{ chatRoomId: string; message: string }>) => {
        state.requestTracker.sendChatMessage.loading = true
      },
      prepare: (chatRoomId: string, message: string) => ({
        payload: { chatRoomId, message },
      }),
    },
    sendChatMessageSuccess(state) {
      state.requestTracker.sendChatMessage.error = null
      state.requestTracker.sendChatMessage.loading = false
    },
    sendChatMessageFailure(state, action) {
      state.requestTracker.sendChatMessage.error = action.payload
      state.requestTracker.sendChatMessage.loading = false
    },
    chatMessageFromSocket(state, action: PayloadAction<ChatRoom>) {
      const existingChatRoom = state.chatRooms.entities[action.payload._id]
      if (existingChatRoom) {
        chatRoomAdapter.upsertOne(state.chatRooms, action.payload)
      }
    },
    confirmReadingMessages(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
  },
})

// actions for socket to emit event
export const chatRoomCreatedSocketEvent = createAction<ChatRoom>(`${ChatRoomModel.reducerName}/chatRoomCreatedSocketEvent`)

export const {
  getChatRoom,
  getChatRoomSuccess,
  getChatRoomFailure,
  sendChatMessage,
  sendChatMessageSuccess,
  sendChatMessageFailure,
  chatMessageFromSocket,
  confirmReadingMessages,
} = chatRoomSlice.actions

export default chatRoomSlice.reducer
